import { useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Users,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  UserPlus,
  Route,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import CreateParticipantModal from '@/components/CreateParticipantModal';
import DeleteParticipantModal from '@/components/DeleteParticipantModal';
import ParticipantStatusBadge from '@/components/ParticipantStatusBadge';
import {
  useParticipants,
  useUpdateParticipant,
  type SortField,
  type SortDir,
  type ParticipantUpdateInput,
} from '@/hooks/useParticipants';
import { useDigitalCenters } from '@/hooks/useDigitalCenters';
import type { Participant } from 'shared';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-PT');
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

function nullable(val: string): string | null {
  return val.trim() === '' ? null : val.trim();
}

// ─── Sort icon ────────────────────────────────────────────────────────────────

interface SortIconProps {
  field: SortField;
  sortBy: SortField;
  sortDir: SortDir;
}

function SortIcon({ field, sortBy, sortDir }: SortIconProps) {
  if (field !== sortBy) return <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground/40" />;
  return sortDir === 'ASC'
    ? <ChevronUp className="w-3.5 h-3.5 text-primary" />
    : <ChevronDown className="w-3.5 h-3.5 text-primary" />;
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

interface EditModalProps {
  participant: Participant;
  onClose: () => void;
  onSaved: () => void;
}

function EditModal({ participant, onClose, onSaved }: EditModalProps) {
  const update = useUpdateParticipant();
  // Every centre, for the centro habitual select. The network is small.
  const centersQuery = useDigitalCenters({ limit: 100 });
  const centers = centersQuery.data?.digitalCenters ?? [];

  const [form, setForm] = useState<ParticipantUpdateInput>({
    nome: participant.nome,
    apelido: participant.apelido,
    genero: participant.genero,
    dataNascimento: toDateInputValue(participant.dataNascimento),
    email: participant.email ?? '',
    telemovel: participant.telemovel ?? '',
    numeroCc: participant.numeroCc ?? '',
    habilitacoesLiterarias: participant.habilitacoesLiterarias ?? '',
    situacaoLaboral: participant.situacaoLaboral ?? '',
    escola: participant.escola ?? '',
    eeNome: participant.eeNome ?? '',
    eeTelemovel: participant.eeTelemovel ?? '',
    eeEmail: participant.eeEmail ?? '',
    eeNumeroCc: participant.eeNumeroCc ?? '',
    morada: participant.morada ?? '',
    localidade: participant.localidade ?? '',
    autorizacaoImagem: participant.autorizacaoImagem,
    autorizacaoEe: participant.autorizacaoEe,
    estadoRegisto: participant.estadoRegisto,
    digitalCenterId: participant.digitalCenterId ?? null,
  });

  const set = (key: keyof ParticipantUpdateInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: ParticipantUpdateInput = {
      ...form,
      email: nullable(form.email as string),
      telemovel: nullable(form.telemovel as string),
      numeroCc: nullable(form.numeroCc as string),
      habilitacoesLiterarias: nullable(form.habilitacoesLiterarias as string),
      situacaoLaboral: nullable(form.situacaoLaboral as string),
      escola: nullable(form.escola as string),
      eeNome: nullable(form.eeNome as string),
      eeTelemovel: nullable(form.eeTelemovel as string),
      eeEmail: nullable(form.eeEmail as string),
      eeNumeroCc: nullable(form.eeNumeroCc as string),
      morada: nullable(form.morada as string),
      localidade: nullable(form.localidade as string),
    };

    update.mutate(
      { id: participant.id, data: payload },
      { onSuccess: () => { onSaved(); onClose(); } }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="text-lg font-semibold text-foreground">
            Editar Participante — <span className="text-primary">{participant.nome} {participant.apelido}</span>
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {update.isError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {update.error?.message ?? 'Erro ao guardar.'}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="N.User">
              <input
                className="input bg-muted/40 cursor-not-allowed text-muted-foreground tabular-nums"
                value={participant.id}
                readOnly
                disabled
                aria-label="Número de utilizador (não editável)"
              />
            </Field>
            <div />
            <Field label="Nome *">
              <input className="input" value={form.nome} onChange={set('nome')} required />
            </Field>
            <Field label="Apelido *">
              <input className="input" value={form.apelido} onChange={set('apelido')} required />
            </Field>
            <Field label="Data de Nascimento *">
              <input type="date" className="input" value={form.dataNascimento} onChange={set('dataNascimento')} required />
            </Field>
            <Field label="Género *">
              <select className="input" value={form.genero} onChange={set('genero')} required>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="I">Indeterminado</option>
              </select>
            </Field>
            <Field label="Email">
              <input type="email" className="input" value={form.email as string} onChange={set('email')} />
            </Field>
            <Field label="Telemóvel">
              <input className="input" value={form.telemovel as string} onChange={set('telemovel')} />
            </Field>
            <Field label="Número CC">
              <input className="input" value={form.numeroCc as string} onChange={set('numeroCc')} />
            </Field>
            <Field label="Estado">
              {participant.estadoRegisto === 'Suspenso' ? (
                <div className="space-y-1">
                  <div className="input bg-muted/40 cursor-not-allowed text-red-600 dark:text-red-400 font-medium flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    Suspenso
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Para repor o estado usa o campo directamente na base de dados ou contacta um administrador.
                  </p>
                </div>
              ) : (
                <select className="input" value={form.estadoRegisto} onChange={set('estadoRegisto')}>
                  <option value="Pendente">Pendente</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Inativo">Inativo</option>
                </select>
              )}
            </Field>
            <Field label="Centro habitual">
              <select
                className="input"
                value={form.digitalCenterId ?? ''}
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    digitalCenterId: e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
                aria-describedby="centro-habitual-hint"
              >
                <option value="">Sem centro</option>
                {centers.map(c => (
                  <option key={c.id} value={c.id}>{c.centerName}</option>
                ))}
              </select>
              <p id="centro-habitual-hint" className="text-xs text-muted-foreground">
                Definido automaticamente na primeira visita. Determina o modelo do certificado.
              </p>
            </Field>
            <Field label="Morada">
              <input className="input" value={form.morada as string} onChange={set('morada')} />
            </Field>
            <Field label="Localidade">
              <input className="input" value={form.localidade as string} onChange={set('localidade')} />
            </Field>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={form.autorizacaoImagem}
                onChange={e => setForm(p => ({ ...p, autorizacaoImagem: e.target.checked }))}
                className="w-4 h-4 accent-primary"
              />
              Autorização de Imagem
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={form.autorizacaoEe}
                onChange={e => setForm(p => ({ ...p, autorizacaoEe: e.target.checked }))}
                className="w-4 h-4 accent-primary"
              />
              Autorização EE
            </label>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
          <button
            type="submit"
            form="edit-form"
            disabled={update.isPending}
            onClick={handleSubmit}
            className="btn-primary flex items-center gap-2"
          >
            {update.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="label text-xs">{label}</label>
      {children}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;

const COLUMNS: { field: SortField; label: string }[] = [
  { field: 'nome', label: 'Nome' },
  { field: 'apelido', label: 'Apelido' },
  { field: 'data_nascimento', label: 'Data Nasc.' },
  { field: 'email', label: 'Email' },
  { field: 'telemovel', label: 'Telemóvel' },
  { field: 'numero_cc', label: 'Nº CC' },
];

export default function UtilizadoresPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('nome');
  const [sortDir, setSortDir] = useState<SortDir>('ASC');
  const [page, setPage] = useState(0);
  const [editTarget, setEditTarget] = useState<Participant | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Participant | null>(null);
  const [creating, setCreating] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const handleSort = (field: SortField) => {
    if (field === sortBy) {
      setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
    setPage(0);
  };

  const { data, isLoading, isError, error } = useParticipants({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    sortBy,
    sortDir,
    search: debouncedSearch,
  });

  const participants = data?.participants ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <>
      <Helmet>
        <title>Utilizadores | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Users className="w-6 h-6 text-primary" />
              Utilizadores
            </h1>
            <p className="text-muted-foreground mt-1">
              Lista de participantes registados na plataforma.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {total > 0 && (
              <span className="text-sm text-muted-foreground tabular-nums">
                {total} registo{total !== 1 ? 's' : ''}
              </span>
            )}
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="btn-primary flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" />
              Novo participante
            </button>
          </div>
        </div>

        {/* ── Search bar ── */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="search"
            placeholder="Pesquisar por nome, apelido, email, telemóvel ou Nº CC…"
            className="input pl-9 w-full"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
        </div>

        {/* ── Table card ── */}
        <div className="card overflow-hidden">
          {/* Loading overlay */}
          {isLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {/* Error */}
          {isError && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-destructive">
              <AlertTriangle className="w-8 h-8" />
              <p className="text-sm font-medium">{error?.message ?? 'Erro ao carregar utilizadores.'}</p>
            </div>
          )}

          {/* Table */}
          {!isLoading && !isError && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap w-20">
                      N.User
                    </th>
                    {COLUMNS.map(col => (
                      <th
                        key={col.field}
                        className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                        onClick={() => handleSort(col.field)}
                      >
                        <div className="flex items-center gap-1.5">
                          {col.label}
                          <SortIcon field={col.field} sortBy={sortBy} sortDir={sortDir} />
                        </div>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">Aut. EE</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Estado</th>
                    <th className="px-4 py-3 w-20" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {participants.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center text-muted-foreground">
                        {debouncedSearch
                          ? `Nenhum resultado para "${debouncedSearch}".`
                          : 'Sem participantes registados.'}
                      </td>
                    </tr>
                  ) : (
                    participants.map(p => (
                      <tr
                        key={p.id}
                        className="hover:bg-accent/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-muted-foreground tabular-nums font-mono text-xs">
                          {p.id}
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground">
                          <Link
                            to={`/users/${p.id}`}
                            className="hover:text-primary hover:underline underline-offset-2"
                          >
                            {p.nome}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-foreground">{p.apelido}</td>
                        <td className="px-4 py-3 text-muted-foreground tabular-nums">
                          {formatDate(p.dataNascimento)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {p.email ?? <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground tabular-nums">
                          {p.telemovel ?? <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground tabular-nums">
                          {p.numeroCc ?? <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {p.autorizacaoEe
                            ? <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">✓ Sim</span>
                            : <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">✗ Não</span>
                          }
                        </td>
                        <td className="px-4 py-3">
                          <ParticipantStatusBadge status={p.estadoRegisto} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <Link
                              to={`/users/${p.id}`}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                              title="Percurso e dados"
                              aria-label={`Percurso e dados de ${p.nome} ${p.apelido}`}
                            >
                              <Route className="w-4 h-4" />
                            </Link>
                            <button
                              onClick={() => setEditTarget(p)}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(p)}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Pagination ── */}
          {!isLoading && !isError && total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <span className="text-sm text-muted-foreground tabular-nums">
                {from}–{to} de {total}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let p: number;
                    if (totalPages <= 5) {
                      p = i;
                    } else if (page < 3) {
                      p = i;
                    } else if (page >= totalPages - 3) {
                      p = totalPages - 5 + i;
                    } else {
                      p = page - 2 + i;
                    }
                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                          p === page
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                        }`}
                      >
                        {p + 1}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Próximo
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {creating && (
        <CreateParticipantModal
          initialName={search}
          onClose={() => setCreating(false)}
          onCreated={() => setCreating(false)}
        />
      )}
      {editTarget && (
        <EditModal
          participant={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
      {deleteTarget && (
        <DeleteParticipantModal
          participant={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
